<script setup lang="ts">
import { computed } from "vue";
import { useMediaQuery } from "@vueuse/core";
import { useI18n } from "vue-i18n";

import {
  Button,
  FileInput,
  Select,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui";

const props = defineProps<{
  open: boolean;
  status: "idle" | "uploading" | "error";
  progress: number | "indeterminate";
  previewUrl: string | null;
  hasFile: boolean;
  serviceOptions: { value: string; label: string }[];
  selectedServiceId: string | null;
  fileError?: string;
  serviceError?: string;
}>();

const emit = defineEmits<{
  "update:open": [boolean];
  selectFile: [File];
  "update:selectedServiceId": [string | null];
  submit: [];
  cancel: [];
}>();

const { t } = useI18n();

const isDesktop = useMediaQuery("(min-width: 768px)");
const sheetSide = computed(() => (isDesktop.value ? "right" : "bottom") as "right" | "bottom");

const allServiceOptions = computed(() => [
  { value: "", label: t("masters.portfolio.upload.noTag") },
  ...props.serviceOptions,
]);

function onFileSelect(file: File): void {
  emit("selectFile", file);
}

function onServiceChange(value: string | null): void {
  emit("update:selectedServiceId", value === "" ? null : value);
}

function onSubmit(): void {
  emit("submit");
}

function onCancel(): void {
  emit("cancel");
  emit("update:open", false);
}
</script>

<template>
  <Sheet
    :open="props.open"
    @update:open="emit('update:open', $event)"
  >
    <SheetContent :side="sheetSide" class="h-[90dvh] overflow-y-auto md:h-full">
      <SheetHeader>
        <SheetTitle>{{ t("masters.portfolio.upload.title") }}</SheetTitle>
      </SheetHeader>

      <div class="flex flex-col gap-4 p-4">
        <!-- File input -->
        <div class="flex flex-col gap-2">
          <label for="portfolio-file-input" class="text-sm font-medium">{{ t("masters.portfolio.upload.fileLabel") }}</label>
          <FileInput
            id="portfolio-file-input"
            accept="image/jpeg,image/png"
            :status="props.status === 'uploading' ? 'uploading' : props.status === 'error' && props.fileError ? 'error' : 'idle'"
            :progress="props.progress"
            :error="props.fileError"
            @select="onFileSelect"
          />
          <!-- Preview thumbnail -->
          <img
            v-if="props.previewUrl"
            :src="props.previewUrl"
            :alt="t('masters.portfolio.upload.preview')"
            class="mt-1 h-24 w-24 rounded-md object-cover"
          />
        </div>

        <!-- Service tag select -->
        <div class="flex flex-col gap-2">
          <label for="portfolio-service-select" class="text-sm font-medium">{{ t("masters.portfolio.upload.serviceLabel") }}</label>
          <Select
            id="portfolio-service-select"
            :model-value="props.selectedServiceId ?? ''"
            :options="allServiceOptions"
            :placeholder="t('masters.portfolio.upload.noTag')"
            :invalid="!!props.serviceError"
            @update:model-value="onServiceChange"
          />
          <p
            v-if="props.serviceError"
            class="text-sm text-destructive"
          >
            {{ props.serviceError }}
          </p>
        </div>

        <!-- Footer -->
        <div class="flex gap-2 pt-2">
          <Button
            :loading="props.status === 'uploading'"
            :disabled="!props.hasFile || props.status === 'uploading'"
            @click="onSubmit"
          >
            {{ t("masters.portfolio.upload.submit") }}
          </Button>
          <Button variant="ghost" @click="onCancel">
            {{ t("masters.portfolio.upload.cancel") }}
          </Button>
        </div>
      </div>
    </SheetContent>
  </Sheet>
</template>
